<template>
  <MobileSearchPanel
    :visible="visible"
    :placeholder="placeholder"
    :initial-keyword="initialKeyword"
    :history-items="historyItems"
    :hot-search-items="hotSearchItems"
    :hot-search-loading="hotSearchLoading"
    :highlight-suggestions="highlightSuggestions"
    enable-suggestions
    :suggestions="productSuggestions"
    :suggestions-loading="suggestionsPending || suggestionsLoading"
    @update:visible="emit('update:visible', $event)"
    @search="handleSearch"
    @clear-history="confirmClearHistory"
    @keyword-change="handleKeywordChange"
    @suggestion-select="handleSuggestionSelect"
  />
</template>

<script setup>
/**
 * @description 商城全局移动搜索业务容器。负责接口、历史记录和路由，展示交给 MobileSearchPanel。
 */
import { computed, ref, watch } from 'vue'
import MobileSearchPanel from '~/components/mobile/SearchPanel.vue'
import { baseApi } from '~/api/base'
import { useStoreProductSuggestions } from '~/composables/useStoreProductSuggestions'
import { useUserStore } from '~/store/common/user'
import CommonModal from '~/utils/commonModal'
import { BaseMessage } from '~/utils/baseMessage'
import {
  createStoreSearchPayload,
  getSearchHistoryKeyword,
  mergeSearchHistoryLists,
} from '~/composables/useStoreSearchKeyword'
import { STORAGE_NAMES, storageKey } from '~/utils/storageKey'
import { rememberProductEntrySource, TRACK_SOURCE_TYPE } from '~/utils/tracking/sourceContext'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  initialKeyword: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '请输入关键词',
  },
  highlightSuggestions: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:visible'])
const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const historyItems = ref([])
const hotSearchItems = ref([])
const hotSearchLoading = ref(false)
const searchHistoryKey = storageKey(STORAGE_NAMES.SEARCH_HISTORY)
const {
  suggestions: productSuggestions,
  pending: suggestionsPending,
  loading: suggestionsLoading,
  scheduleSuggestions,
  clearSuggestions,
} = useStoreProductSuggestions()

const readLocalHistory = () => {
  if (!import.meta.client) return []
  try {
    return JSON.parse(localStorage.getItem(searchHistoryKey) || '[]')
  } catch {
    return []
  }
}

const saveLocalHistory = () => {
  if (!import.meta.client) return
  localStorage.setItem(searchHistoryKey, JSON.stringify(historyItems.value))
}

const loadHistory = async () => {
  const localHistory = readLocalHistory()
  if (!isLoggedIn.value) {
    historyItems.value = mergeSearchHistoryLists(localHistory)
    return
  }

  try {
    const response = await baseApi.getSearchKeywords()
    const data = Array.isArray(response)
      ? response
      : response?.list || response?.data || response || []
    historyItems.value = mergeSearchHistoryLists(localHistory, Array.isArray(data) ? data : [])
    saveLocalHistory()
  } catch {
    historyItems.value = mergeSearchHistoryLists(localHistory)
  }
}

const loadHotSearch = async () => {
  hotSearchLoading.value = true
  try {
    const response = await baseApi.getHotSearch(1)
    const data = Array.isArray(response) ? response : response?.data || response || []
    hotSearchItems.value = Array.isArray(data) ? data.slice(0, 30) : []
  } catch {
    hotSearchItems.value = []
  } finally {
    hotSearchLoading.value = false
  }
}

const addHistory = (payload) => {
  if (!payload.displayText || !payload.keyword) return
  const item = { displayText: payload.displayText, keyword: payload.keyword }
  historyItems.value = [
    item,
    ...historyItems.value.filter((entry) => getSearchHistoryKeyword(entry) !== item.keyword),
  ].slice(0, 20)
  saveLocalHistory()
  if (isLoggedIn.value) baseApi.recordSearchKeyword(item.keyword).catch(() => {})
}

const handleSearch = (keyword) => {
  const payload = createStoreSearchPayload({
    displayText: keyword,
    hotSearchList: hotSearchItems.value,
  })
  if (!payload.displayText) return
  addHistory(payload)
  navigateTo(`/store/search?q=${encodeURIComponent(payload.displayText)}`)
}

/**
 * @description 根据移动端输入内容调度商品模糊联想
 * @param {string} keyword - 当前搜索框内容
 * @returns {void}
 */
const handleKeywordChange = (keyword) => {
  if (!String(keyword || '').trim()) {
    clearSuggestions()
    return
  }
  scheduleSuggestions(keyword)
}

/**
 * @description 点击商品联想后直接进入商品详情页
 * @param {Record<string, any>} item - 归一化后的商品联想项
 * @returns {void}
 */
const handleSuggestionSelect = (item) => {
  const serviceId = item?.value ?? item?.serviceId ?? item?.id
  if (!serviceId) return
  const keyword = String(item?.label || item?.serviceName || item?.modelNo || '').trim()
  rememberProductEntrySource(serviceId, {
    sourceType: TRACK_SOURCE_TYPE.SEARCH_RESULT,
    sourceId: keyword || 'search_result',
    sourceName: keyword || '搜索结果',
    fromPage: 'search_result',
  })
  clearSuggestions()
  navigateTo(`/store/products/${serviceId}`)
}

const clearHistory = async () => {
  if (isLoggedIn.value) await baseApi.deleteSearchKeywords().catch(() => {})
  historyItems.value = []
  saveLocalHistory()
  BaseMessage.success('清除成功')
}

const confirmClearHistory = () => {
  CommonModal.confirm({
    title: '确认清除',
    content: '确定要清除全部搜索历史吗？',
    type: 'warning',
    okText: '确定',
    cancelText: '取消',
    onOk: clearHistory,
  })
}

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      Promise.all([loadHistory(), loadHotSearch()])
      return
    }
    clearSuggestions()
  },
  { immediate: true }
)
</script>
